import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type { ClassSlotWithDetail, MoveSlotMembersResult } from '@repo/shared-types';
import type { CreateSlotInput, UpdateSlotInput, MoveSlotMembersInput } from '@repo/validation';
import { toast } from 'sonner';
import { invalidateGettingStarted } from './useGettingStarted';

export function useSlots(
  opts?: { teacherId?: string; status?: 'ACTIVE' | 'ARCHIVED'; branchId?: string },
  // Second argument, NOT a field on `opts` — `enabled` is a fetch policy, not
  // part of the request, and folding it into the key would split the cache.
  // Mirrors useIncome/useExpenses in hooks/useBookkeeping.ts.
  options?: { enabled?: boolean },
) {
  const teacherId = opts?.teacherId;
  const status = opts?.status;
  const branchId = opts?.branchId;
  const params = new URLSearchParams();
  if (teacherId) params.set('teacherId', teacherId);
  if (status) params.set('status', status);
  if (branchId) params.set('branchId', branchId);
  const qs = params.toString();
  return useQuery<ClassSlotWithDetail[]>({
    queryKey: ['slots', { teacherId: teacherId ?? null, status: status ?? null, branchId: branchId ?? null }],
    queryFn: () => fetchApi(qs ? `/slots?${qs}` : '/slots'),
    enabled: options?.enabled ?? true,
  });
}
export function useSlot(id: string | null) {
  return useQuery<ClassSlotWithDetail>({ queryKey: ['slots', id], queryFn: () => fetchApi(`/slots/${id}`), enabled: id !== null });
}
const inval = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['slots'] });
  qc.invalidateQueries({ queryKey: ['sessions'] });
  // Joining/leaving a slot changes an enrollment's effective start date + session
  // count shown on the Student detail enrollment card — refresh it too.
  qc.invalidateQueries({ queryKey: ['enrollments'] });
  // Fix wave (review I1) — every khung giờ mutation is reachable from the
  // class layer (ClassDetailPage's per-slot pencil, TimetableGridTab), and
  // a class's derived name/roster/displayName all read `['classes']`,
  // while the students list's Lớp column reads `['students']`. Missing
  // these left both stale after a save that toasted success — worst on
  // archive, where archiving the last ACTIVE slot of a group auto-archives
  // the group and the list kept rendering the now-404 class.
  qc.invalidateQueries({ queryKey: ['classes'] });
  qc.invalidateQueries({ queryKey: ['students'] });
  // Checklist steps 2 (a slot exists) and 4 (someone is placed in one) — both
  // creating a slot and joining one route through here.
  invalidateGettingStarted(qc);
};

export function useCreateSlot() {
  const qc = useQueryClient();
  return useMutation({
    // THI-267 Task 6: the find-or-create MERGE (`outcome: 'existed'`) is
    // gone — an ACTIVE twin at this teacher-time is now a 409
    // (assertNoTimeConflict, raised before createSlotInGroup is ever
    // called) — and so is the ARCHIVED-twin revival branch (`outcome:
    // 'revived'`): `createSlotInGroup` always creates a fresh slot now.
    // Fix wave (review M7): the API still sends `outcome` on the response,
    // but nothing on this side reads it any more, so the type carrying it
    // is gone too rather than left dead on both ends.
    mutationFn: (data: CreateSlotInput) => fetchApi<ClassSlotWithDetail>('/slots', { method: 'POST', body: JSON.stringify(data) }),
    // Fix wave (review I11): this toast said "Đã tạo lớp" — every sibling
    // in this file was reworded to "khung giờ" (Task 17) except this one,
    // teaching back the exact lớp/khung-giờ confusion the rename exists to
    // remove.
    onSuccess: () => { inval(qc); toast.success('Đã tạo khung giờ'); },
    onError: (e) => toast.error(e.message || 'Không thể tạo khung giờ'),
  });
}
export function useUpdateSlot(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSlotInput) => fetchApi<ClassSlotWithDetail>(`/slots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { inval(qc); toast.success('Đã cập nhật khung giờ'); },
    onError: (e) => toast.error(e.message || 'Không thể cập nhật'),
  });
}
export function useArchiveSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<{ archived: boolean }>(`/slots/${id}/archive`, { method: 'POST' }),
    onSuccess: () => { inval(qc); toast.success('Đã lưu trữ khung giờ'); },
    onError: (e) => toast.error(e.message || 'Không thể lưu trữ'),
  });
}
export function useUnarchiveSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<{ unarchived: boolean }>(`/slots/${id}/unarchive`, { method: 'POST' }),
    onSuccess: () => { inval(qc); toast.success('Đã khôi phục khung giờ'); },
    onError: (e) => toast.error(e.message || 'Không thể khôi phục'),
  });
}
export function useJoinSlot(slotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ enrollmentId, startDate }: { enrollmentId: string; startDate?: string }) =>
      fetchApi<ClassSlotWithDetail>(`/slots/${slotId}/members`, { method: 'POST', body: JSON.stringify({ enrollmentId, ...(startDate ? { startDate } : {}) }) }),
    onSuccess: () => { inval(qc); toast.success('Đã xếp vào lớp'); },
    onError: (e) => toast.error(e.message || 'Không thể xếp lớp'),
  });
}
export function useLeaveSlot(slotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enrollmentId: string) => fetchApi(`/slots/${slotId}/members/${enrollmentId}`, { method: 'DELETE' }),
    onSuccess: () => { inval(qc); toast.success('Đã rời lớp'); },
    onError: (e) => toast.error(e.message || 'Không thể rời lớp'),
  });
}

// Bulk reassignment behind "chế độ xếp lớp" — one call moves the whole ticked
// group in one server-side transaction (see apps/api's POST /slots/move and
// the design doc). onError intentionally shows e.message as-is: the server's
// 400 names the offending students in Vietnamese, and that message is more
// useful to the teacher than any generic string this hook could substitute.
export function useMoveSlotMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MoveSlotMembersInput) => fetchApi<MoveSlotMembersResult>('/slots/move', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (result) => { inval(qc); toast.success(`Đã chuyển ${result.moved} học viên`); },
    onError: (e) => toast.error(e.message || 'Không thể chuyển học viên'),
  });
}
